import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Search, ChevronLeft, ChevronRight, AlertTriangle } from 'lucide-react';
import { Card } from '../../components/ui/Card';
import { Badge } from '../../components/ui/Badge';
import { Loading } from '../../components/ui/Loading';
import { EmptyState } from '../../components/ui/EmptyState';
import { usePayments } from '../../hooks/usePayments';
import { formatCurrency, formatDate } from '../../lib/utils';
import { CreditCard } from 'lucide-react';

const PaymentList: React.FC = () => {
    const [filters, setFilters] = useState({
        page: 1,
        per_page: 20,
        search: '',
        final_state: '',
        provider: '',
        has_risks: undefined as boolean | undefined,
    });

    const { data, isLoading } = usePayments(filters);

    const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
        setFilters((prev) => ({ ...prev, search: e.target.value, page: 1 }));
    };

    const handleFilterChange = (key: string, value: any) => {
        setFilters((prev) => ({ ...prev, [key]: value, page: 1 }));
    };

    if (isLoading && filters.page === 1) {
        return <Loading fullScreen message="Loading payments..." />;
    }

    return (
        <div className="space-y-6">
            {/* Header */}
            <div>
                <h1 className="text-2xl font-bold text-gray-900">Payments</h1>
                <p className="text-gray-600 mt-1">View and manage all payment transactions</p>
            </div>

            {/* Filters */}
            <Card>
                <div className="space-y-4">
                    {/* Search */}
                    <div className="relative">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                        <input
                            type="text"
                            value={filters.search}
                            onChange={handleSearch}
                            placeholder="Search by transaction ID, email..."
                            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                        />
                    </div>

                    {/* Filter Row */}
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                        <select
                            value={filters.final_state}
                            onChange={(e) => handleFilterChange('final_state', e.target.value)}
                            className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                        >
                            <option value="">All Final States</option>
                            <option value="provider_success">Provider Success</option>
                            <option value="revenue_confirmed">Revenue Confirmed</option>
                            <option value="lost">Lost</option>
                            <option value="duplicated">Duplicated</option>
                            <option value="provider_failed">Provider Failed</option>
                        </select>

                        <select
                            value={filters.provider}
                            onChange={(e) => handleFilterChange('provider', e.target.value)}
                            className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                        >
                            <option value="">All Providers</option>
                            <option value="stripe">Stripe</option>
                            <option value="iyzico">Iyzico</option>
                            <option value="paytr">PayTR</option>
                            <option value="paypal">PayPal</option>
                            <option value="adyen">Adyen</option>
                        </select>

                        <select
                            value={filters.has_risks === undefined ? '' : filters.has_risks.toString()}
                            onChange={(e) => handleFilterChange('has_risks', e.target.value ? e.target.value === 'true' : undefined)}
                            className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                        >
                            <option value="">All Payments</option>
                            <option value="true">With Risks</option>
                            <option value="false">Without Risks</option>
                        </select>

                        <button
                            onClick={() => setFilters({ page: 1, per_page: 20, search: '', final_state: '', provider: '', has_risks: undefined })}
                            className="btn-secondary"
                        >
                            Clear Filters
                        </button>
                    </div>
                </div>
            </Card>

            {/* Payments Table */}
            {!data || !data.data || data.data.length === 0 ? (
                <Card>
                    <EmptyState
                        icon={CreditCard}
                        title="No payments found"
                        description="No payment transactions match your current filters. Try adjusting your search criteria."
                    />
                </Card>
            ) : (
                <>
                    <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
                        <div className="overflow-x-auto">
                            <table className="w-full">
                                <thead className="bg-gray-50 border-b border-gray-200">
                                    <tr>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Transaction
                                        </th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Customer
                                        </th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Amount
                                        </th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Provider
                                        </th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Status
                                        </th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Date
                                        </th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Risks
                                        </th>
                                    </tr>
                                </thead>
                                <tbody className="bg-white divide-y divide-gray-200">
                                    {data?.data?.map((payment) => (
                                        <tr key={payment.id} className="hover:bg-gray-50 transition-colors">
                                            <td className="px-6 py-4">
                                                <Link to={`/payments/${payment.id}`} className="font-mono text-sm text-primary-600 hover:text-primary-700">
                                                    {payment.id}
                                                </Link>
                                            </td>
                                            <td className="px-6 py-4">
                                                <div>
                                                    <div className="text-sm font-medium text-gray-900">{payment.external_user_id || 'N/A'}</div>
                                                    <div className="text-sm text-gray-500">{payment.metadata?.customer_email || 'N/A'}</div>
                                                </div>
                                            </td>
                                            <td className="px-6 py-4">
                                                <div className="text-sm font-semibold text-gray-900">
                                                    {formatCurrency(payment.expected_amount, payment.currency)}
                                                </div>
                                            </td>
                                            <td className="px-6 py-4">
                                                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800 capitalize">
                                                    {payment.provider}
                                                </span>
                                            </td>
                                            <td className="px-6 py-4">
                                                <Badge variant="status" status={payment.status}>
                                                    {payment.status}
                                                </Badge>
                                            </td>
                                            <td className="px-6 py-4 text-sm text-gray-500">
                                                {formatDate(payment.created_at)}
                                            </td>
                                            <td className="px-6 py-4">
                                                {payment.has_unresolved_risks ? (
                                                    <div className="flex items-center gap-2">
                                                        {payment.unresolved_risks_count >= 3 ? (
                                                            <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800">
                                                                <AlertTriangle className="w-3 h-3" />
                                                                {payment.unresolved_risks_count} Critical
                                                            </span>
                                                        ) : payment.unresolved_risks_count >= 2 ? (
                                                            <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium bg-orange-100 text-orange-800">
                                                                <AlertTriangle className="w-3 h-3" />
                                                                {payment.unresolved_risks_count} High
                                                            </span>
                                                        ) : (
                                                            <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                                                                <AlertTriangle className="w-3 h-3" />
                                                                {payment.unresolved_risks_count} Medium
                                                            </span>
                                                        )}
                                                    </div>
                                                ) : payment.is_healthy ? (
                                                    <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                                        ✓ Healthy
                                                    </span>
                                                ) : (
                                                    <span className="text-sm text-gray-400">—</span>
                                                )}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>

                    {/* Pagination */}
                    {data && data.meta && data.meta.last_page > 1 && (
                        <div className="flex items-center justify-between">
                            <div className="text-sm text-gray-600">
                                Showing {data.meta.from} to {data.meta.to} of {data.meta.total} results
                            </div>
                            <div className="flex items-center gap-2">
                                <button
                                    onClick={() => setFilters((prev) => ({ ...prev, page: prev.page - 1 }))}
                                    disabled={filters.page === 1}
                                    className="p-2 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    <ChevronLeft className="w-5 h-5" />
                                </button>
                                <span className="text-sm text-gray-600">
                                    Page {data.meta.current_page} of {data.meta.last_page}
                                </span>
                                <button
                                    onClick={() => setFilters((prev) => ({ ...prev, page: prev.page + 1 }))}
                                    disabled={filters.page === data.meta.last_page}
                                    className="p-2 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    <ChevronRight className="w-5 h-5" />
                                </button>
                            </div>
                        </div>
                    )}
                </>
            )}
        </div>
    );
};

export default PaymentList;
